/**
 * The fit control's state, lifted so the top bar can hold the button.
 *
 * The control belongs in the top bar, beside the density switch, the fullscreen
 * toggle and the theme: that cluster is *how this console is displayed*, which
 * is exactly what fitting a wall is. It was first put in the wall's own control
 * strip, which was wrong in the ordinary way — it sat among Tek/Bölünmüş/Izgara
 * and read as a fourth layout rather than as a display preference.
 *
 * But the state cannot live in the top bar. Which wall, how many tiles and how
 * big its column is are the page's facts, and the top bar is drawn once for
 * every screen. So the page keeps the state and *registers* it here; the bar
 * reads the registration and draws a button, or draws nothing where no screen
 * has registered one. Same shape as `useBroadcast` and `usePaneChord`: inert
 * outside a provider, and the component that draws the control never has to
 * know which screen it is acting on.
 *
 * Registered as primitives rather than as an object, and that is not fussiness:
 * the page recomputes this every render, so an object would be a new identity
 * five times a minute and the effect that publishes it would re-run each time.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export interface WallFitControl {
  /** Whether the screen's wall can run out of room at all. False in single and
   *  split, which divide the height between one or two panes. The button stays
   *  on screen and goes disabled — a control that comes and goes as the layout
   *  is switched is one an operator stops looking for. */
  fittable: boolean
  enabled: boolean
  setEnabled: (on: boolean) => void
  /** The size the fit arrived at, and whether it was enough. Null while off. */
  font: number | null
  fits: boolean
  columns: number
  rows: number
}

const Read = createContext<WallFitControl | null>(null)
const Write = createContext<(c: WallFitControl | null) => void>(() => {})

export function WallFitProvider({ children }: { children: ReactNode }) {
  const [control, setControl] = useState<WallFitControl | null>(null)
  return (
    <Write.Provider value={setControl}>
      <Read.Provider value={control}>{children}</Read.Provider>
    </Write.Provider>
  )
}

/** What the top bar draws, or null where no screen has a wall. */
export function useWallFit(): WallFitControl | null {
  return useContext(Read)
}

/** A screen with a wall, publishing its fit control to the bar. */
export function useRegisterWallFit(c: WallFitControl): void {
  const publish = useContext(Write)
  const { fittable, enabled, setEnabled, font, fits, columns, rows } = c
  const value = useMemo(
    () => ({ fittable, enabled, setEnabled, font, fits, columns, rows }),
    [fittable, enabled, setEnabled, font, fits, columns, rows],
  )
  useEffect(() => {
    publish(value)
    // Leaving the screen takes the button with it. Without this the bar would
    // go on offering a fit for a wall that is no longer on the page, and
    // pressing it would move every terminal in the console to a size computed
    // from a column nobody is looking at.
    return () => publish(null)
  }, [publish, value])
}
